import { computed, nextTick, reactive, type ComputedRef, type Ref } from 'vue';
import { isGeoItemMetaComment } from 'src/lib/geoItemHelper';
import type { MovieList } from 'src/models/movie';
import type {
  GeoItemsMeta,
  GIComment,
  GIMovieList,
  GIResource,
  MapSelectOpts,
  TryRefreshCurrentSelectedElemInfoParams,
} from 'src/models/geoItem';
import type { IExtremeMapRef, Location, MapElemInfo, QueryArea } from 'src/models';
import type { SimpleModalState } from 'src/models/top';

export interface MapElemState {
  mapSelectedElemInfo: MapElemInfo<GIResource | Location> | null;
  mapDeleteConfirmElem: (MapElemInfo<GIComment> & { confirmMessage: string }) | null;
  mapSelectedElemCreateFailed: number;
  mapSelectedElemUpdateFailed: number;
  getGeoItemByIdFailed: number;
}

interface UseMapSelectionCoreDeps {
  geoItemsMeta: GeoItemsMeta;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  resizeMap: () => void;
  simpleModal: SimpleModalState;
  afterMoviePlayClick: (obj: MovieList) => void;
  doAreaMovieSearch: (queryArea: QueryArea) => void;
  isMovieSearchResultModeNone: () => boolean;
  isMovieSearchResultModeArea: () => boolean;
  getAreaQueryRadiusMeter: () => number;
}

export interface MapSelectionCoreContext {
  mapElem: MapElemState;
  setMapSelectedElemInfo: (val: MapElemInfo<GIResource | Location> | null, opts?: MapSelectOpts) => void;
  tryRefreshCurrentSelectedElemInfo: (params: TryRefreshCurrentSelectedElemInfoParams) => void;
  afterDataLayerItemClick: (dataName: string, obj: GIResource, opts?: MapSelectOpts) => void;
  currentElemInfoComponent: ComputedRef<string>;
  getNewObjForMapSelectedElem: (queryArea: QueryArea) => MapElemInfo<Location>;
  onClickMap: (clickedLocation: Location) => Promise<void>;
  onMapItemClicked: (obj: MapElemInfo<GIResource>) => void;
  onMapAllDeselected: () => void;
  playMapSelectedElem: (obj: MapElemInfo<MovieList>) => Promise<void>;
  onMapSelectedElemAreaSizeChange: () => Promise<void>;
}

/**
 * 地図クリック/選択のコアロジック。
 * mapElem（選択中要素の共有状態）を保有し、コメントCRUD・タブ内ナビゲーションからは
 * このコンテキストの setMapSelectedElemInfo / afterDataLayerItemClick / mapElem に一方向依存する。
 */
export function useMapSelectionCore(deps: UseMapSelectionCoreDeps): MapSelectionCoreContext {
  const { geoItemsMeta, refExtremeMap, resizeMap, simpleModal } = deps;

  const mapElem = reactive<MapElemState>({
    mapSelectedElemInfo: null,
    mapDeleteConfirmElem: null,
    mapSelectedElemCreateFailed: 0,
    mapSelectedElemUpdateFailed: 0,
    getGeoItemByIdFailed: 0,
  });

  const hasLocation = (candidate: unknown): candidate is Location => {
    return candidate !== null && typeof candidate === 'object' && 'lat' in candidate && 'lon' in candidate;
  };
  const isMovieList = (resource: unknown): resource is GIMovieList => {
    return resource !== null && typeof resource === 'object' && 'movies' in resource;
  };

  const setMapSelectedElemInfo = (
    val: MapElemInfo<GIResource | Location> | null,
    opts: MapSelectOpts = {},
  ) => {
    mapElem.mapSelectedElemInfo = val;
    if (val?.dataName === 'comment' && mapElem.mapSelectedElemInfo) {
      const metaItem = geoItemsMeta.map[val.dataName];
      if (isGeoItemMetaComment(metaItem)) {
        mapElem.mapSelectedElemInfo.relativeComments = metaItem.layerManager.getRelativeComments(val.data as GIComment);
      }
    }
    nextTick(() => {
      resizeMap();
      if (opts.moveCenter && refExtremeMap.value && val && !isMovieList(val.data)) {
        // val.data は「新規作成」フロー(getNewObjForMapSelectedElem)では素の Location だが、
        // GIResource(車両・付箋等)では座標が .location にネストされているため使い分ける
        // (GIResourceの全メンバーがlocation: Locationを持つことは確認済み)
        const location = hasLocation(val.data) ? val.data : (val.data as Exclude<GIResource, GIMovieList>).location;
        refExtremeMap.value.moveCenterTo(location);
      }
    });
  };

  const tryRefreshCurrentSelectedElemInfo = (params: TryRefreshCurrentSelectedElemInfoParams) => {
    if (!mapElem.mapSelectedElemInfo) return;

    const { dataName, resources, idFieldName = 'id' } = params;
    const idFieldKey = idFieldName as keyof GIResource;
    const selectedData = mapElem.mapSelectedElemInfo.data;
    if (mapElem.mapSelectedElemInfo.dataName === dataName &&
      !hasLocation(selectedData) &&
      !resources.find(e => mapElem.mapSelectedElemInfo && e[idFieldKey] === selectedData[idFieldKey])
    ) {
      setMapSelectedElemInfo(null);
    }
  };

  const afterDataLayerItemClick = (dataName: string, obj: GIResource, opts: MapSelectOpts = {}) => {
    if (obj.isSelected && refExtremeMap.value) {
      refExtremeMap.value.deselectLayersExcept(dataName);
      setMapSelectedElemInfo({
        dataName: dataName,
        data: obj,
      }, { moveCenter: !!opts.moveCenter });
    } else {
      setMapSelectedElemInfo(null);
    }
  };

  const currentElemInfoComponent = computed(() => {
    if (!mapElem.mapSelectedElemInfo) return 'map-elem-info-none';

    if (mapElem.mapSelectedElemInfo.dataName === 'comment') return 'map-elem-info-comment-container';

    return 'map-elem-info-' + mapElem.mapSelectedElemInfo.dataName;
  });

  const getNewObjForMapSelectedElem = (queryArea: QueryArea): MapElemInfo<Location> => {
    return {
      dataName: 'new',
      data: {
        lat: queryArea.lat,
        lon: queryArea.lon,
      },
    };
  };
  const onClickMap = async(clickedLocation: Location) => {
    const queryArea = {
      lat: clickedLocation.lat,
      lon: clickedLocation.lon,
      radius: deps.getAreaQueryRadiusMeter(),
    };
    if (deps.isMovieSearchResultModeNone() || deps.isMovieSearchResultModeArea()) {
      // まだ検索してない、もしくは現在が範囲検索の場合
      deps.doAreaMovieSearch(queryArea);
      // 地図下部の新規作成エリアを表示する
      setMapSelectedElemInfo(
        getNewObjForMapSelectedElem(queryArea));
    } else {
      // 現在が範囲検索ではない場合は一旦確認してから実施する
      if (refExtremeMap.value) {
        refExtremeMap.value.showQueryArea(queryArea);
      }
      simpleModal.message = '範囲検索に切り替えてもよろしいですか？';
      simpleModal.onClose = () => {
        deps.doAreaMovieSearch(queryArea);
        // 地図下部の新規作成エリアを表示する
        setMapSelectedElemInfo(
          getNewObjForMapSelectedElem(queryArea));
        simpleModal.show = false;
      };
      simpleModal.onDismiss = () => {
        // ピン位置だけ残してみる
        if (refExtremeMap.value) {
          refExtremeMap.value.showQueryPin(queryArea);
        }
        // 地図下部の新規作成エリアを表示する
        setMapSelectedElemInfo(getNewObjForMapSelectedElem(queryArea));
        simpleModal.show = false;
      };
      simpleModal.show = true;
    }
  };
  const onMapItemClicked = (obj: MapElemInfo<GIResource>) => {
    const defaultDataList = ['movie'];
    if (
      !defaultDataList.includes(obj.dataName) &&
      !geoItemsMeta.map[obj.dataName]
    ) {
      console.warn('onMapItemClicked. Unknown resource type', obj.dataName);
      return;
    }
    afterDataLayerItemClick(obj.dataName, obj.data);
  };
  const onMapAllDeselected = () => {
    // 地図下部の表示を消す
    setMapSelectedElemInfo(null);
  };
  const playMapSelectedElem = async(obj: MapElemInfo<MovieList>) => {
    deps.afterMoviePlayClick(obj.data);
  };
  const onMapSelectedElemAreaSizeChange = async() => {
    nextTick(() => {
      resizeMap();
    });
  };

  return {
    mapElem,
    setMapSelectedElemInfo,
    tryRefreshCurrentSelectedElemInfo,
    afterDataLayerItemClick,
    currentElemInfoComponent,
    getNewObjForMapSelectedElem,
    onClickMap,
    onMapItemClicked,
    onMapAllDeselected,
    playMapSelectedElem,
    onMapSelectedElemAreaSizeChange,
  };
}
